import { useEffect, useRef, useState } from 'react';
import { ChromePicker } from 'react-color';
import { mdiWater } from '@mdi/js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FieldValues, useController } from 'react-hook-form';

import { ColorInputButton } from 'common/forms/Field/ColorPickerField/ColorInputButton';
import { Icon } from 'common/components/Icon';
import { Tooltip } from 'common/components/Tooltip';
import { FieldLabel } from 'common/components/FieldLabel';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

type TPopoverPosition = 'bottom-start' | 'bottom-end';

interface IProps {
    fullWidth?: boolean;
    label?: string;
    popoverPosition?: TPopoverPosition;
    tooltip?: string;
    name: string;
}

export const ColorPickerField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    fullWidth,
    label,
    popoverPosition,
    tooltip,
}: IProps & TControllerProps<TFieldValues>) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(undefined);
    const [timer, setTimer] = useState<NodeJS.Timeout>();

    const control = asTypedControl<string | undefined>(name, formControl);
    const { field } = useController({ control, name });

    useEffect(
        () => () => {
            if (timer) clearTimeout(timer);
        },
        [timer]
    );

    /*
     * Handles outside click of the component to close the picker
     */
    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            const { current: wrapperElement } = wrapperRef || {};

            if (!wrapperElement || !event.target) return;

            if (!wrapperElement.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', onClick);

        return () => {
            document.removeEventListener('mousedown', onClick);
        };
    }, []);

    const position =
        !popoverPosition || popoverPosition === 'bottom-start'
            ? { left: 0 }
            : { right: 0 };

    const color = field.value?.replace('#', '');

    return (
        <Tooltip placement="top" title={tooltip || ''}>
            <Box ref={wrapperRef} sx={{ position: 'relative', width: '100%' }}>
                {!!label && (
                    <FieldLabel>
                        <Typography>{label}</Typography>
                    </FieldLabel>
                )}

                <ColorInputButton
                    backgroundColor={`#${color || 'ffffff'}`}
                    sx={fullWidth ? { width: '100%' } : undefined}
                    onClick={() => setOpen(true)}
                >
                    <Icon path={mdiWater} size="3rem" />
                </ColorInputButton>

                {open && (
                    <>
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                            }}
                            onClick={() => {
                                setOpen(false);
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                zIndex: 9999,
                                ...position,
                            }}
                        >
                            <ChromePicker
                                {...field}
                                disableAlpha
                                color={field.value}
                                onChange={(color) => {
                                    // Remove hashtag from new color
                                    const newColor = color.hex.replace('#', '');

                                    // Add debounce before setting new field value
                                    if (timer) clearTimeout(timer);

                                    setTimer(
                                        setTimeout(() => {
                                            field.onChange(newColor);
                                        }, 100)
                                    );
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Tooltip>
    );
};
